import React, { Component, useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Avatar from '@mui/material/Avatar';
import Accordion from 'react-bootstrap/Accordion';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemText,
  Divider,
  Tabs,
  Tab,
  CardActionArea,
  ButtonBase,
} from "@mui/material";
import { useAuth } from "../../auth/AuthContext";
import LoadingPage from "../../Loader";
import { initiateDPOPayment, redirectToDPOPayment } from "../registration/DPOPaymentService";
import Invoice from "../registration/Invoice";
import primaryBackground from "../../assets/Backgrounds/primaryBg.svg";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import BallotIcon from "@mui/icons-material/Ballot";
import SchoolIcon from '@mui/icons-material/School';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import { fetchUserData, fetchCourseMaterials, fetchAssessments, fetchStudentScores } from "./fetchService";
import AssessmentList from "./AssessmentList";
import { getYouTubeVideoId } from "./utils";

// Error Boundary Component
class ErrorBoundary extends Component {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ p: 4 }}>
          <Typography variant="h6" color="error">
            Something went wrong: {this.state.error?.message || "Unknown error"}
          </Typography>
          <Button onClick={() => window.location.reload()} variant="contained" sx={{ mt: 2 }}>
            Reload Page
          </Button>
        </Box>
      );
    }
    return this.props.children;
  }
}

const UserDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [studentData, setStudentData] = useState(null);
  const [moduleData, setModuleData] = useState([]);
  const [students, setStudents] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [selectedModule, setSelectedModule] = useState(null);
  const [courseMaterials, setCourseMaterials] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [scores, setScores] = useState([]);
  const [formData, setFormData] = useState({
    trans_token: "",
    trans_Ref: "",
    trans_status: "",
    trans_result_explanation: ""
  });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const { getData, updateData, addData } = useAuth();
  const authStatus = JSON.parse(localStorage.getItem("authStatus")) || {};
  const user = useMemo(() => authStatus.user || {}, [authStatus.user]);
  const navigate = useNavigate();

  useEffect(() => {
    //console.log("Fetching user data...", { email: user.email, type: user.type });
    fetchUserData(
      user,
      getData,
      (data) => {
        //console.log("Setting userData:", data);
        if (JSON.stringify(data) !== JSON.stringify(userData)) {
          setUserData(data);
        }
      },
      (data) => {
        //console.log("Setting studentData:", data);
        if (JSON.stringify(data) !== JSON.stringify(studentData)) {
          setStudentData(data);
        }
      },
      (data) => {
        //console.log("Setting moduleData:", data);
        if (JSON.stringify(data) !== JSON.stringify(moduleData)) {
          setModuleData(data);
        }
      },
      (data) => {
        //console.log("Setting students:", data);
        if (JSON.stringify(data) !== JSON.stringify(students)) {
          setStudents(data);
        }
      },
      setLoading
    ).catch((error) => {
      console.error("fetchUserData error:", error);
      setSnackbarMessage(`Failed to load user data: ${error.message}`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      setLoading(false);
    });
  }, [user.email, user.type, getData]);

  useEffect(() => {
    //console.log("useEffect for materials/assessments/scores:", {
    //   tabValue,
    //   selectedModuleId: selectedModule?.id,
    //   studentCourse: studentData?.course,
    //   studentId: studentData?.id
    // });
    const moduleId = selectedModule?.id || studentData?.course;
    if (moduleId) {
      if (tabValue === 1 || tabValue === 4) {
        setLoading(true);
        Promise.all([
          fetchCourseMaterials(getData, moduleId),
          fetchAssessments(getData, moduleId)
        ])
          .then(([materials, assessments]) => {
            //console.log("Fetched materials:", materials.length, "Fetched assessments:", assessments.length);
            setCourseMaterials((prev) => JSON.stringify(prev) !== JSON.stringify(materials) ? materials : prev);
            setAssessments((prev) => JSON.stringify(prev) !== JSON.stringify(assessments) ? assessments : prev);
            setLoading(false);
          })
          .catch((error) => {
            console.error("Error fetching materials/assessments:", error);
            setCourseMaterials([]);
            setAssessments([]);
            setSnackbarMessage(`Failed to load materials: ${error.message}`);
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
            setLoading(false);
          });
      } else if (tabValue === 3 && studentData?.id) {
        setLoading(true);
        fetchStudentScores(getData, studentData.id, moduleId)
          .then((scoresData) => {
            //console.log("Fetched scores:", scoresData);
            setScores((prev) => JSON.stringify(prev) !== JSON.stringify(scoresData) ? scoresData : prev);
            setLoading(false);
          })
          .catch((error) => {
            console.error("Error fetching scores:", error);
            setScores([]);
            setSnackbarMessage(`Failed to load grades: ${error.message}`);
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
            setLoading(false);
          });
      }
    } else {
      setCourseMaterials([]);
      setAssessments([]);
      setScores([]);
    }
  }, [selectedModule?.id, tabValue, studentData?.course, studentData?.id, getData]);

  const handlePayment = useCallback(async () => {
    if (!studentData || studentData.dueToUs <= 0) {
      setSnackbarMessage("No outstanding balance to settle");
      setSnackbarSeverity("info");
      setSnackbarOpen(true);
      return;
    }

    if (!studentData.trans_token) {
      const paymentData = {
        amount: studentData.dueToUs,
        currency: "NAD",
        customerEmail: user.email,
        customerFirstName: userData?.firstName || userData?.fullName?.split(" ")[0] || "",
        customerLastName: userData?.lastName || userData?.fullName?.split(" ").slice(1).join(" ") || "",
        customerPhone: studentData.cell,
        selectedModules: studentData.enrolledModules
          ? studentData.enrolledModules.map((m) => m.name)
          : [],
        serviceType: "3854",
        serviceDescription: "Account Settlement",
      };

      try {
        setLoading(true);
        const result = await initiateDPOPayment(paymentData);
        if (result.success) {
          setFormData({
            trans_token: result.token,
            trans_Ref: result.transectionRef,
            trans_status: '900',
            trans_result_explanation: 'Transaction not paid yet'
          });
          const response = await updateData(
            "proccess.php",
            "students_V2",
            {
              trans_token: result.token,
              trans_Ref: result.transectionRef,
              trans_status: '900',
              trans_result_explanation: 'Transaction not paid yet'
            },
            String(studentData.id)
          );
          if (response.success) {
            setStudentData((prev) => JSON.stringify(prev) !== JSON.stringify(response.data) ? response.data : prev);
            redirectToDPOPayment(`https://secure.3gdirectpay.com/dpopayment.php?ID=${result.token}`);
          } else {
            setSnackbarMessage(`Failed to update transaction: ${response.message}`);
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
          }
        } else {
          setSnackbarMessage(`Payment initiation error: ${result.message}`);
          setSnackbarSeverity("error");
          setSnackbarOpen(true);
        }
      } catch (error) {
        setSnackbarMessage(`Payment error: ${error.message}`);
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      } finally {
        setLoading(false);
      }
    } else {
      const status = `${studentData.trans_status}`;
      const explanation = studentData.trans_result_explanation || '';

      const statusMap = {
        '000': 'Transaction Paid',
        '001': 'Authorized',
        '002': 'Transaction overpaid/underpaid',
        '003': 'Pending Bank',
        '005': 'Queued Authorization',
        '007': 'Pending Split Payment (Part Payment Transactions not fully paid)',
        '801': 'Request missing company token',
        '802': 'Company token does not exist',
        '803': 'No request or error in Request type name',
        '804': 'Error in XML',
        '900': 'Transaction not paid yet',
        '901': 'Transaction declined',
        '902': 'Data mismatch in one of the fields - field (' + explanation + ')',
        '903': 'The transaction passed the Payment Time Limit',
        '904': 'Transaction cancelled',
        '950': 'Request missing transaction level mandatory fields – field (' + explanation + ')'
      };

      const needsPaymentStatuses = ['900', '003', '005', '007'];
      if (needsPaymentStatuses.includes(status)) {
        try {
          setLoading(true);
          const paymentUrl = `https://secure.3gdirectpay.com/dpopayment.php?ID=${studentData.trans_token}`;
          if (paymentUrl) {
            redirectToDPOPayment(paymentUrl);
          } else {
            setSnackbarMessage('Failed to retrieve payment URL');
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
          }
        } catch (error) {
          setSnackbarMessage(`Error redirecting to payment: ${error.message}`);
          setSnackbarSeverity("error");
          setSnackbarOpen(true);
        } finally {
          setLoading(false);
        }
      } else {
        const msg = statusMap[status] || 'Unknown status';
        setSnackbarMessage(`Payment status: ${msg}. No action needed.`);
        setSnackbarSeverity("info");
        setSnackbarOpen(true);
      }
    }
  }, [studentData, user, userData, initiateDPOPayment, redirectToDPOPayment, updateData]);

  const handleTabChange = useCallback((event, newValue) => {
    //console.log("Tab changed to:", newValue);
    setTabValue(newValue);
    if (newValue !== 1 && newValue !== 4) {
      setSelectedModule(null);
      setCourseMaterials([]);
      setAssessments([]);
      setScores([]);
    }
  }, []);

  const handleAddStudent = useCallback(() => {
    navigate("/add-student");
  }, [navigate]);

  const handleStudentClick = useCallback((student) => {
    //console.log("Student clicked:", student.id);
    setStudentData(student);
    setTabValue(1);
    setSelectedModule(null);
  }, []);

  const handleModuleViewClick = useCallback((module) => {
    //console.log("Module view clicked:", module.id);
    setSelectedModule(module);
    setTabValue(4);
  }, []);

  const handleModuleClick = useCallback((module) => {
    //console.log("Module clicked:", module.id);
    setSelectedModule(module);
    setTabValue(1);
  }, []);

  const submitAssessment = async (assessmentId, answers) => {
    try {
      const response = await addData(
        "proccess.php",
        "assess_attempt_V2",
        {
          assessment_ID: assessmentId,
          student_ID: studentData?.id,
          attempt: JSON.stringify(answers),
        }
      );
      if (response.success) {
        setSnackbarMessage("Assessment submitted successfully!");
        setSnackbarSeverity("success");
        if (tabValue === 3) {
          const moduleId = selectedModule?.id || studentData?.course;
          fetchStudentScores(getData, studentData?.id, moduleId)
            .then((scoresData) => setScores((prev) => JSON.stringify(prev) !== JSON.stringify(scoresData) ? scoresData : prev))
            .catch((error) => {
              setSnackbarMessage(`Failed to refresh grades: ${error.message}`);
              setSnackbarSeverity("error");
              setSnackbarOpen(true);
            });
        }
      } else {
        setSnackbarMessage(`Failed to submit assessment: ${response.message}`);
        setSnackbarSeverity("error");
      }
    } catch (error) {
      setSnackbarMessage(`Error submitting assessment: ${error.message}`);
      setSnackbarSeverity("error");
    }
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  if (loading) {
    return <LoadingPage />;
  }

  return (
    <ErrorBoundary>
      <Box
        width={"100%"}
        sx={{
          minHeight: "100vh",
          backgroundImage: `url(${primaryBackground})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          p: { xs: 2, sm: 4 },
        }}
      >
        <Box sx={{ textAlign: "center", mb: 4, paddingTop: "1rem" }} />
        <Typography
          variant="h4"
          gutterBottom
          sx={{ fontSize: { xs: "1.5rem", sm: "2rem", md: "2.5rem" } }}
        >
          Hi, {userData?.firstName || userData?.fullName?.split(" ")[0] || "User"}{" "}
          {userData?.lastName || userData?.fullName?.split(" ").slice(1).join(" ") || ""} 👋
        </Typography>

        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            mb: 4,
            "& .MuiTab-root": {
              fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" },
              padding: { xs: "6px 15px", sm: "12px 16px" },
              minWidth: { xs: 80, sm: 100 },
            },
            "& .MuiTab-iconWrapper": {
              fontSize: { xs: "1rem", sm: "1.25rem", md: "1.5rem" },
            },
            "& .MuiTabs-indicator": {
              backgroundColor: "#137734ff",
            },
            "& .MuiTabs-scrollButtons": {
              display: { xs: "flex", sm: "none" },
            },
          }}
        >
          <Tab
            label="PROFILE"
            icon={<AccountCircleIcon />}
            iconPosition="start"
            sx={{
              textTransform: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          />
          <Tab
            label="MOODLE"
            icon={<ViewModuleIcon />}
            iconPosition="start"
            sx={{ textTransform: "none" }}
          />
          <Tab
            label="ACCOUNT"
            icon={<AccountBalanceWalletIcon />}
            iconPosition="start"
            sx={{ textTransform: "none" }}
          />
          <Tab
            label="GRADES"
            icon={<SchoolIcon />}
            iconPosition="start"
            sx={{ textTransform: "none" }}
          />
          {tabValue === 4 && (
            <Tab
              label="STUDY MATERIALS"
              icon={<AutoStoriesIcon />}
              iconPosition="start"
              sx={{ textTransform: "none" }}
            />
          )}
        </Tabs>

        {tabValue === 0 && (
          <Box>
            <Card sx={{ mb: 4 }}>
              <CardContent sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', justifyContent: 'start' }}>
                <Avatar
                  alt={userData?.firstName || "User"}
                  src="/static/images/avatar/1.jpg"
                  sx={{ width: { xs: 80, sm: 100 }, height: { xs: 80, sm: 100 } }}
                />
                <Box>
                  <Typography>
                    <strong>Type:</strong>{" "}
                    {user.type === "student" ? "Student" : "Parent"}
                  </Typography>
                  <Typography>
                    <strong>Email:</strong> {userData?.email || "N/A"}
                  </Typography>
                  {user.type === "student" && studentData && (
                    <>
                      <Typography>
                        <strong>Age:</strong> {studentData.age || "N/A"}
                      </Typography>
                      <Typography>
                        <strong>School:</strong> {studentData.school || "N/A"}
                      </Typography>
                      <Typography>
                        <strong>Grade:</strong> {studentData.grade || "N/A"}
                      </Typography>
                      <Typography>
                        <strong>Address:</strong> {studentData.physicalAddress || "N/A"}
                      </Typography>
                    </>
                  )}
                  {user.type === "parent" && (
                    <Typography>
                      <strong>Phone:</strong> {userData?.cell || "N/A"}
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>

            {user.type === "parent" && (
              <Card sx={{ mb: 4 }}>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="h6">My Students</Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleAddStudent}
                    >
                      Add New Student
                    </Button>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                    {students.length > 0 ? (
                      students.map((student) => (
                        <Card key={student.id} sx={{ width: { xs: "100%", sm: 250 }, backgroundColor: '#137734ff' }}>
                          <CardActionArea
                            onClick={() => handleStudentClick(student)}
                          >
                            <CardContent>
                              <Typography variant="subtitle1" fontWeight="bold" color="#fff">
                                {student.firstName} {student.lastName}
                              </Typography>
                              <Typography variant="body2" color="#fff">
                                Grade: {student.grade || "N/A"}
                              </Typography>
                              <Typography variant="body2" color="#fff">
                                School: {student.school || "N/A"}
                              </Typography>
                            </CardContent>
                          </CardActionArea>
                        </Card>
                      ))
                    ) : (
                      <Typography>No students registered yet.</Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            )}

            {user.type === "student" && studentData && (
              <Card sx={{ mb: 4 }}>
                <CardContent>
                  <Box display={"flex"} justifyContent={"space-between"}>
                    <Typography variant="h6">Enrolled Modules</Typography>
                    <BallotIcon />
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                    {studentData.enrolledModules && studentData.enrolledModules.length > 0 ? (
                      studentData.enrolledModules.map((module) => (
                        <Card
                          key={module.id}
                          sx={{
                            flex: 1,
                            minWidth: { xs: '100%', sm: '500px', md: '500px' },
                            backgroundColor: "blueviolet",
                            color: "#fff",
                          }}
                        >
                          <CardActionArea
                            onClick={() => handleModuleClick(module)}
                          >
                            <CardContent>
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                }}
                              >
                                <Typography variant="subtitle1" fontWeight="bold">
                                  {module.module_name || "Unnamed Module"}
                                </Typography>
                                <MenuBookIcon />
                              </Box>
                              <Box display={'flex'} flexDirection={{ xs: 'column', sm: 'row' }} gap={1}>
                                <Box width={{ xs: '100%', sm: '500px' }}>
                                  <img
                                    src={`https://mindsinaction.com.na/assets/media/pictures/${module.image || 'default.jpg'}`}
                                    style={{ maxWidth: '100%' }}
                                    alt={module.module_name || "Module"}
                                    onError={(e) => e.target.src = '/static/images/fallback.jpg'} // Fallback image
                                  />
                                </Box>
                                <Box>
                                  <Typography textAlign={'justify'}>{module.description || "No description available"}</Typography>
                                </Box>
                              </Box>
                            </CardContent>
                          </CardActionArea>
                        </Card>
                      ))
                    ) : (
                      <Typography>No modules enrolled yet.</Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            )}
          </Box>
        )}

        {tabValue === 1 && (
          <Box>
            {user.type === "student" && studentData ? (
              <Card sx={{ mb: 4 }}>
                <CardContent>
                  <Typography variant="h6">
                    Study Materials -{" "}
                    {selectedModule
                      ? `Module: ${selectedModule.module_name || "Unnamed"}`
                      : "Enrolled Modules"}
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <List>
                    {courseMaterials.length > 0 ? (
                      courseMaterials.map((material) => (
                        <Box key={material.id || Math.random()}>
                          <Accordion>
                            <Accordion.Item eventKey={material.id || Math.random()}>
                              <Accordion.Header>{material.title || `Material ${material.id || "Unknown"}`}</Accordion.Header>
                              <Accordion.Body>
                                <Box display={"flex"} flexWrap={'wrap'} gap={2}>
                                  <Box sx={{ flex: 1, minWidth: '200px' }}>
                                    <Box
                                      sx={{
                                        width: '100%',
                                        height: { xs: "200px", sm: "300px" },
                                        borderRadius: "10px",
                                        overflow: "hidden",
                                        '& iframe': {
                                          border: 'none',
                                        },
                                      }}
                                    >
                                      {material.link && (
                                        <iframe
                                          width="100%"
                                          height="100%"
                                          src={`https://www.youtube.com/embed/${getYouTubeVideoId(material.link)}`}
                                          title={material.title || "YouTube video"}
                                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                          allowFullScreen
                                        />
                                      )}
                                    </Box>
                                  </Box>
                                  <Box sx={{ flex: 1, minWidth: '200px' }}>
                                    <Typography textAlign={'justify'}>{material.note_content || "No notes available"}</Typography>
                                    <ButtonBase 
                                    href={`https://www.mindsinaction.com.na/assets/document/${material.image}`}
                                    sx={{backgroundColor: 'blue', color: '#fff', padding: '1rem', marginTop: '2rem', borderRadius: '.5rem'}}>
                                      <Typography>Download Notes</Typography>
                                    </ButtonBase>
                                  </Box>
                                </Box>
                                <AssessmentList assessments={assessments} submitAssessment={submitAssessment} student={studentData} />
                              </Accordion.Body>
                            </Accordion.Item>
                          </Accordion>
                        </Box>
                      ))
                    ) : (
                      <ListItem>
                        <ListItemText primary="No study materials available." />
                      </ListItem>
                    )}
                  </List>
                </CardContent>
              </Card>
            ) : (
              <Card sx={{ mb: 4 }}>
                <CardContent>
                  <Box>
                    <Typography variant="h5" mb={4}>
                      STUDENT: {studentData?.firstName || "N/A"} {studentData?.lastName || ""}
                    </Typography>
                  </Box>
                  <Box display={"flex"} justifyContent={"space-between"}>
                    <Typography variant="h6">Enrolled Modules</Typography>
                    <BallotIcon />
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                    {studentData?.enrolledModules && studentData.enrolledModules.length > 0 ? (
                      studentData.enrolledModules.map((module) => (
                        <Card
                          key={module.id}
                          sx={{
                            flex: 1,
                            minWidth: { xs: '300px', sm: '500px', md: '500px' },
                            maxWidth: { md: '50%' },
                            backgroundColor: "blueviolet",
                            color: "#fff",
                          }}
                        >
                          <CardActionArea
                            onClick={() => handleModuleViewClick(module)}
                          >
                            <CardContent>
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                }}
                              >
                                <Typography variant="subtitle1" fontWeight="bold">
                                  {module.module_name || "Unnamed Module"}
                                </Typography>
                                <MenuBookIcon />
                              </Box>
                              <Box display={'flex'} flexDirection={{ xs: 'column', sm: 'row' }} gap={1}>
                                <Box width={{ xs: '100%', sm: '500px' }}>
                                  <img
                                    src={`https://mindsinaction.com.na/assets/media/pictures/${module.image || 'default.jpg'}`}
                                    style={{ maxWidth: '100%' }}
                                    alt={module.module_name || "Module"}
                                    onError={(e) => e.target.src = '/static/images/fallback.jpg'}
                                  />
                                </Box>
                                <Box>
                                  <Typography textAlign={'justify'}>{module.description || "No description available"}</Typography>
                                </Box>
                              </Box>
                            </CardContent>
                          </CardActionArea>
                        </Card>
                      ))
                    ) : (
                      <Typography>No modules enrolled yet.</Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            )}
          </Box>
        )}

        {tabValue === 2 && (
          <Box display={"flex"} flexWrap={"wrap"}>
            <Card sx={{ mb: 4, minWidth: "300px", flex: 1 }}>
              <CardContent>
                <Typography variant="h6">Account Details</Typography>
                <Divider sx={{ my: 1 }} />
                <Typography>
                  <strong>Status:</strong>{" "}
                  {studentData?.status === 0 ? "Pending" : "Active"}
                </Typography>
                <Typography>
                  <strong>Due Amount:</strong> N$
                  {studentData?.dueToUs?.toFixed(2) || "0.00"}
                </Typography>
                {studentData?.dueToUs > 0 && (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handlePayment}
                    sx={{ mt: 2 }}
                  >
                    Settle Account
                  </Button>
                )}
                {studentData?.dueToUs <= 0 && (
                  <Typography color="success.main">
                    No outstanding balance.
                  </Typography>
                )}
              </CardContent>
            </Card>
            <Invoice
              userData={userData}
              studentData={studentData}
              students={students}
              userType={user.type}
              modules={moduleData}
            />
          </Box>
        )}

        {tabValue === 3 && (
          <Box display={"flex"} flexWrap={"wrap"}>
            <Card sx={{ mb: 4, minWidth: "300px", flex: 1 }}>
              <CardContent>
                <Typography variant="h6">
                  Grades for {selectedModule ? selectedModule.module_name || "Unnamed" : "Enrolled Module"}
                </Typography>
                <Divider sx={{ my: 1 }} />
                {loading ? (
                  <Skeleton variant="rectangular" height={200} />
                ) : scores.length > 0 ? (
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Assessment Name</TableCell>
                          <TableCell>Score</TableCell>
                          <TableCell>Submission Date</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {scores.map((score) => (
                          <TableRow key={score.id || Math.random()}>
                            <TableCell>{score.assessment_name || "Unknown"}</TableCell>
                            <TableCell>{score.score != null ? `${score.score}%` : "N/A"}</TableCell>
                            <TableCell>
                              {score.submission_date
                                ? new Date(score.submission_date).toLocaleString()
                                : "N/A"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography>No grades available yet.</Typography>
                )}
              </CardContent>
            </Card>
            <Invoice
              userData={userData}
              studentData={studentData}
              students={students}
              userType={user.type}
              modules={moduleData}
            />
          </Box>
        )}

        {tabValue === 4 && (
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant="h6">
                Study Materials -{" "}
                {selectedModule
                  ? `Module: ${selectedModule.module_name || "Unnamed"}`
                  : "Enrolled Modules"}
              </Typography>
              <Divider sx={{ my: 1 }} />
              <List>
                {courseMaterials.length > 0 ? (
                  courseMaterials.map((material) => (
                    <Box key={material.id || Math.random()}>
                      <Accordion>
                        <Accordion.Item eventKey={material.id || Math.random()}>
                          <Accordion.Header>{material.title || `Material ${material.id || "Unknown"}`}</Accordion.Header>
                          <Accordion.Body>
                            <Box display={"flex"} flexWrap={'wrap'} gap={2}>
                              <Box sx={{ flex: 1, minWidth: '200px' }}>
                                <Box
                                  sx={{
                                    width: '100%',
                                    height: { xs: "200px", sm: "300px" },
                                    borderRadius: "10px",
                                    overflow: "hidden",
                                  }}
                                >
                                  {material.link && (
                                    <iframe
                                      width="100%"
                                      height="100%"
                                      src={`https://www.youtube.com/embed/${getYouTubeVideoId(material.link)}`}
                                      title={material.title || "YouTube video"}
                                      frameBorder="0"
                                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                      allowFullScreen
                                    />
                                  )}
                                </Box>
                              </Box>
                              <Box sx={{ flex: 1, minWidth: '200px' }}>
                                <Typography textAlign={'justify'}>{material.note_content || "No notes available"}</Typography>
                                <ButtonBase sx={{backgroundColor: 'blue', color: '#fff', padding: '1rem', marginTop: '2rem', borderRadius: '.5rem'}}>
                                  <Typography>Download Notes</Typography>
                                </ButtonBase>
                              </Box>
                            </Box>
                            <AssessmentList assessments={assessments} submitAssessment={submitAssessment} />
                          </Accordion.Body>
                        </Accordion.Item>
                      </Accordion>
                    </Box>
                  ))
                ) : (
                  <ListItem>
                    <ListItemText primary="No study materials available." />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>
        )}

        <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
          <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    </ErrorBoundary>
  );
};

export default UserDashboard;